import {getFormValues} from 'redux-form';
import {List, Map} from 'immutable';
import moment from 'moment-timezone';

import createSelector from 'web-app/util/custom-create-selector';
import {getInvoiceRangeFromFormData} from 'web-app/react/components/form-elements/invoice-period-picker/helpers';
import * as BillPayerSelectors from 'web-app/react/entities/bill-payers/selectors';
import * as DatePeriodPickerHelpers from 'web-app/react/components/form-elements/invoice-period-picker/helpers';
import * as InstitutionSelectors from 'web-app/react/entities/institutions/selectors';
import InvoiceBehaviorIds from 'web-app/behaviors/invoice-behavior-ids';
import InstitutionBehaviorIds from 'web-app/behaviors/institution-behavior-ids';
import {type RootState} from 'web-app/react/redux/main-reducer';
import {hasValue} from 'web-app/util/typescript';

import * as Constants from './constants';
import * as DebtReportSelectors from '../selectors';
import {fetchPeriodFromSelectedPeriod, shouldShowWarning, hasError} from './helpers';
import {type Invoice} from './types';

export const getBatchInvoiceDateValues = (state: RootState) =>
    getFormValues(Constants.INVOICE_MONTH_SELECTOR_FORM_NAME)(state) as DatePeriodPickerHelpers.InvoicingFormData;
const getBillPayerIds = (state: RootState) => state.plans.batchInvoice.billPayerIds;
const getInvoicesData = (state: RootState) => state.plans.batchInvoice.invoices;
export const getPreviewInvoicesMap = (state: RootState) => state.plans.batchInvoice.previewInvoicesMap;

export const isUpdatingData = createSelector(
    (state: RootState) => state.plans.batchInvoice,
    batchInvoiceState => batchInvoiceState.updatingBillPayerIds || batchInvoiceState.updatingInvoicePreviews,
);

export const isUpdatingDetailsData = createSelector(
    (state: RootState) => state.plans.batchInvoice,
    batchInvoiceState => batchInvoiceState.updatingBillPayerIds,
);

export const didFetchAllData = createSelector(
    (state: RootState) => state.plans.batchInvoice,
    batchInvoiceState => batchInvoiceState.didFetchBillPayerIds && batchInvoiceState.didFetchInvoicePreviews,
);

const billPayerInvoicesToAmount = (invoiceData: any) =>
    !invoiceData || invoiceData.error
        ? 0
        : invoiceData.invoices.map(i => Number(i.amount)).reduce((prev, current) => prev + current, 0);

const mapInvoicePreviewsToTotalAmount = (previews: any) =>
    previews.size === 0 ? NaN : previews.reduce((prev, current) => prev + billPayerInvoicesToAmount(current), 0);

export const getAllBillPayerIds = createSelector(getBillPayerIds, billPayerIds => billPayerIds);

export const getBillPayerIdsWithItemsToInvoice = createSelector(getPreviewInvoicesMap, previews =>
    previews
        .filter(preview => !preview.error)
        .keySeq()
        .toList(),
);

export const getAllInvalidBillPayerIds = createSelector(getPreviewInvoicesMap, previews =>
    previews
        .filter(preview => hasError(preview))
        .keySeq()
        .toList(),
);

export const getSelectedBillPayers = createSelector(
    (state: RootState) => state.plans.batchInvoice.selectedBillPayers,
    selectedBillPayers =>
        selectedBillPayers
            .filter(isSelected => isSelected)
            .keySeq()
            .toArray(),
);

export const getInvalidSelection = createSelector(getAllInvalidBillPayerIds, getSelectedBillPayers, (all, selected) =>
    selected.filter(s => all.indexOf(s) > -1),
);

export const areAllBillPayersSelected = createSelector(
    getBillPayerIds,
    getSelectedBillPayers,
    (billPayerIds, selectedBillPayerIds) => {
        return billPayerIds.size === selectedBillPayerIds.length;
    },
);

const getSelectedInvoicePreviews = createSelector(
    getSelectedBillPayers,
    getPreviewInvoicesMap,
    (selectedIds, previewInvoices) => Map(selectedIds.map(id => [id, previewInvoices.get(id)])),
);

export const getTotalSelectedPreviewAmount = createSelector(
    getSelectedInvoicePreviews,
    mapInvoicePreviewsToTotalAmount,
);

export const getTotalPreviewAmount = createSelector(getPreviewInvoicesMap, mapInvoicePreviewsToTotalAmount);

export const canBatchInvoiceWeekBehavior = createSelector(
    (state: RootState) => state.plans.batchInvoice.behaviors,
    behaviors => behaviors.behaviorForId(InvoiceBehaviorIds.CanBatchInvoiceWeek),
);

export const billPayerCreationValidationPayload = createSelector(
    (state: RootState) => state.plans.batchInvoice.behaviors,
    behaviors => behaviors.behaviorForId(InstitutionBehaviorIds.CanCreateBillPayer)?.payload,
);

export const showWeekPeriodOption = createSelector(canBatchInvoiceWeekBehavior, behavior => Boolean(behavior));

export const firstDayOfWeek = createSelector(
    canBatchInvoiceWeekBehavior,
    behavior => behavior?.payload?.firstDayOfWeek,
);

export const getInitialDateFormValues = createSelector(
    firstDayOfWeek,
    (state: RootState, props: {institutionId: string}) => props.institutionId,
    (firstDayOfWeek, institutionId) => {
        const startOfNextMonth = moment().startOf('month').add(1, 'month');
        const endOfNextMonth = startOfNextMonth.clone().endOf('month');

        const initialPeriod = {
            from: startOfNextMonth.format('YYYY-MM-DD'),
            to: endOfNextMonth.format('YYYY-MM-DD'),
        };

        /**
         * We include `institutionId` in the `initialDateFormValues` to
         * make sure the form calls `onChange` when this changes. This
         * is necessary, since the `onChange` is responsible for data fetching.
         *
         * If you'd like to try and improve this, please ensure it works on
         * NavigationV2 when switching between an institution on the page.
         */
        return {
            institutionId,
            notify: true,
            due: startOfNextMonth.format('YYYY-MM-DD'),
            ...DatePeriodPickerHelpers.getInitialValues(initialPeriod, firstDayOfWeek),
        };
    },
);

export const fetchPeriod = createSelector(getBatchInvoiceDateValues, batchInvoiceDateValues =>
    batchInvoiceDateValues
        ? fetchPeriodFromSelectedPeriod(getInvoiceRangeFromFormData(batchInvoiceDateValues), 'YYYY-MM-DD')
        : {},
);

export const getDebtDetailsData = createSelector(
    getBillPayerIds,
    BillPayerSelectors.getBillPayers,
    fetchPeriod,
    didFetchAllData,
    getInvoicesData,
    getPreviewInvoicesMap,
    (billPayerIds, allBillPayers, fetchPeriod, didFetchAllData, invoiceData, previewInvoicesMap) => {
        if (!didFetchAllData) {
            return {data: List(), months: List()};
        }

        const billPayers = billPayerIds.map(bpid => allBillPayers.get(bpid));

        const invoiceDataDetails = invoiceData.mapEntries(([billPayerId, invoices]) => {
            const invoicesByMonth = List(invoices)
                .map((invoice: Invoice) => {
                    const invoiceDate = moment(invoice.invoiceDate);
                    return {
                        id: invoice.invoiceId,
                        amount: invoice.total,
                        date: invoice.invoiceDate,
                        invoiceDate: invoiceDate.format('YYYY-MM'),
                        billPayerId,
                    };
                })
                .groupBy(invoice => invoice.invoiceDate)
                .toJS();

            return [
                billPayerId,
                {
                    invoiced: invoicesByMonth,
                },
            ];
        });
        const debtDetailsData = DebtReportSelectors.debtDetailsData(billPayers, invoiceDataDetails, fetchPeriod);

        const newDebtDetailsData = {
            ...debtDetailsData,
            data: debtDetailsData.data.map(debtDetailsDataEntry => ({
                billPayer: debtDetailsDataEntry.billPayer,
                details: debtDetailsDataEntry.details,
                shouldShowWarning: shouldShowWarning(previewInvoicesMap, debtDetailsDataEntry, debtDetailsData.months),
                shouldShowInfo: shouldShowInfo(debtDetailsDataEntry, debtDetailsData.months),
            })),
        };

        return newDebtDetailsData;
    },
);

export const showEmptyState = createSelector(
    didFetchAllData,
    getBillPayerIds,
    state => state.plans.batchInvoice.warnings,
    (didFetchAllData, billPayerIds, warnings) => {
        /**
         * Ideally the empty state should show if there exists no bill payers AND there are no plans on anyone in the institution.
         * However, since it's kind of expensive to query for all plans, we're loosening the condition a bit.
         */
        return didFetchAllData && billPayerIds.size === 0 && warnings.size === 0;
    },
);

const shouldShowInfo = (debtDetailsDataEntry: any, months: List<string>) => {
    const dataDetailsInvoiced = debtDetailsDataEntry.details.invoiced;
    const monthWithInvoicedData = months.find(month => dataDetailsInvoiced && dataDetailsInvoiced.get(month));
    return !hasValue(monthWithInvoicedData);
};

export const showAutomaticPaymentsLegend = createSelector(
    InstitutionSelectors.canSupportAutomaticPayments,
    canSupportAutomaticPayments => {
        return canSupportAutomaticPayments;
    },
);

export const showManualDirectDebitLegend = createSelector(
    InstitutionSelectors.showBillPayerBankDetails,
    showBillPayerBankDetails => {
        return showBillPayerBankDetails;
    },
);

export const showLegend = createSelector(
    showAutomaticPaymentsLegend,
    showManualDirectDebitLegend,
    (showAutomaticPaymentsLegend, showManualDirectDebitLegend) => {
        return showAutomaticPaymentsLegend || showManualDirectDebitLegend;
    },
);
